import { Component, OnInit, OnDestroy, ElementRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import * as moment from 'moment';
import { MsgContext, GuestContactVO, ConversationSummaryVO, DueTaskVO, MsgOrigin, ReservationVO, TaskVO, QuoteVO, OrderVO } from './unified-inbox.models';
import { UnifiedInboxService } from './unified-inbox.service';
import { TaskService } from '@app/_components/task';
import { environment } from '@environments/environment';

interface NavItem {
    label: string;
    icon: string;
    mailbox: string;
    count?: number;
    active?: boolean;
}

interface LabelItem {
    label: string;
    color: string;
    count?: number;
}

@Component({
    selector: 'app-unified-inbox',
    templateUrl: './unified-inbox.component.html',
    styleUrls: ['./unified-inbox.component.scss'],
    standalone: false
})
export class UnifiedInboxComponent implements OnInit, OnDestroy {

    context: number = MsgContext.RESERVATIONS;
    activeView: 'conversation' | 'all' = 'conversation';
    activeConversationId = '';
    searchQuery = '';

    searchResults: any[] = [];
    searchLoading = false;
    searchError = '';
    private searchDebounce: ReturnType<typeof setTimeout> | null = null;

    private refreshInterval: ReturnType<typeof setInterval> | null = null;
    private sseSource: EventSource | null = null;

    convLabels: { [id: string]: string[] } = {};
    availableLabels: string[] = [];
    // Maps label name → tagId so deleteManagedLabel can call the tag delete endpoint.
    private labelTagIds: { [name: string]: number } = {};

    contact: GuestContactVO = null;
    guestReservations: ReservationVO[] = [];
    guestTasks: TaskVO[] = [];
    guestQuotes: QuoteVO[] = [];
    guestOrders: OrderVO[] = [];

    contextName = 'Reservations';

    conversations: ConversationSummaryVO[] = [];
    convLoading = false;
    convLoadingMore = false;
    convError = '';
    hasMore = false;
    private oldestDate: string | null = null;
    readIds: Set<string> = new Set();

    mailboxItems: NavItem[] = [
        { label: 'Inbox', icon: '📥', mailbox: 'inbox', active: true },
        { label: 'Starred', icon: '⭐', mailbox: 'starred' },
        { label: 'Sent', icon: '📤', mailbox: 'sent' },
        { label: 'Archive', icon: '🗄', mailbox: 'archive' },
        { label: 'Drafts', icon: '📝', mailbox: 'drafts' }
    ];

    contextItems: NavItem[] = [
        { label: 'Reservations', icon: '', mailbox: 'ctx-7' },
        { label: 'Homeowners', icon: '', mailbox: 'ctx-8' },
        { label: 'BizDev', icon: '', mailbox: 'ctx-9' },
        { label: 'Developers', icon: '', mailbox: 'ctx-4' }
    ];

    labelItems: LabelItem[] = [];

    activeMailbox = 'inbox';
    activeFolder = '';
    ctxDropOpen = false;

    // Manage labels dialog
    manageLabelsOpen = false;
    manageLabelNewName = '';

    // Hamburger / nav collapse
    navOpen = true;

    // Filters panel
    filtersOpen = false;
    filterChannel = '';
    filterStatus = '';

    // Active nav filters (label, channel, property)
    activeNavLabel = '';
    activeNavChannel = '';
    activeNavProperty = '';

    // Quick-filter chips: All / New Mail / Follow-ups
    activeQuickFilter: 'all' | 'new-mail' | 'follow-ups' = 'all';

    // Task detail panel — shown when a task row with no linked conversation is clicked
    activeTaskDetail: DueTaskVO | null = null;

    // Compose panel
    composingNew = false;
    composeTo = '';
    composeSubject = '';
    composeBody = '';
    composeError = '';
    composeSending = false;

    followupCount = 0;

    // ── Panel resize ──────────────────────────────────────────────────────────
    private resizeState: { col: 'nav' | 'conv'; startX: number; startW: number } | null = null;

    private readonly onResizeMove = (e: MouseEvent) => {
        if (!this.resizeState) { return; }
        const dx   = e.clientX - this.resizeState.startX;
        const newW = this.resizeState.col === 'nav'
            ? Math.max(160, Math.min(400, this.resizeState.startW + dx))
            : Math.max(220, Math.min(600, this.resizeState.startW + dx));
        this.el.nativeElement.style.setProperty(
            this.resizeState.col === 'nav' ? '--ums-nav-w' : '--ums-conv-w',
            newW + 'px'
        );
    };

    private readonly onResizeUp = () => {
        if (!this.resizeState) { return; }
        const prop = this.resizeState.col === 'nav' ? '--ums-nav-w' : '--ums-conv-w';
        const key  = 'ums-' + this.resizeState.col + '-w';
        localStorage.setItem(key, this.el.nativeElement.style.getPropertyValue(prop));
        this.el.nativeElement.querySelector('.ums-layout')?.classList.remove('resizing');
        document.removeEventListener('mousemove', this.onResizeMove);
        document.removeEventListener('mouseup',   this.onResizeUp);
        this.resizeState = null;
    };

    startResize(event: MouseEvent, col: 'nav' | 'conv'): void {
        event.preventDefault();
        const sel = col === 'nav' ? '.ums-nav' : '.ums-convlist';
        const el  = this.el.nativeElement.querySelector(sel) as HTMLElement;
        this.resizeState = { col, startX: event.clientX, startW: el.offsetWidth };
        this.el.nativeElement.querySelector('.ums-layout')?.classList.add('resizing');
        document.addEventListener('mousemove', this.onResizeMove);
        document.addEventListener('mouseup',   this.onResizeUp);
    }
    // ─────────────────────────────────────────────────────────────────────────

    constructor(private route: ActivatedRoute, private unifiedInboxService: UnifiedInboxService, private taskService: TaskService, private el: ElementRef) {}

    ngOnInit(): void {
        // Restore saved panel widths
        const savedNav  = localStorage.getItem('ums-nav-w');
        const savedConv = localStorage.getItem('ums-conv-w');
        if (savedNav)  { this.el.nativeElement.style.setProperty('--ums-nav-w',  savedNav); }
        if (savedConv) { this.el.nativeElement.style.setProperty('--ums-conv-w', savedConv); }

        this.route.params.subscribe(params => {
            const ctx = parseInt(params['context'], 10);
            if (!isNaN(ctx)) {
                this.context = ctx;
                this.contextName = this.getContextName(ctx);
            }
            this.loadConversations();
        });
        this.connectSSE();
        this.loadFollowupCount();
        this.loadLabelDefinitions();
    }

    private loadFollowupCount(): void {
        const currentUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
        const userId = currentUser?.userId;
        if (!userId) { return; }
        this.taskService.getTaskSummary(userId).subscribe({
            next: (res: any) => {
                const summary = res?.data?.task;
                this.followupCount = summary?.taskFollowupToday || 0;
            },
            error: () => { this.followupCount = 0; }
        });
    }

    private readonly defaultLabelItems: LabelItem[] = [
        { label: 'VIP',               color: '#c5a258' },
        { label: 'Late Check-in',     color: '#e06338' },
        { label: 'Billing',           color: '#4896f0' },
        { label: 'Complaint',         color: '#e04e4e' },
        { label: 'Booking Confirmed', color: '#2dbe60' },
        { label: 'Returning Guest',   color: '#8a6d2e' }
    ];

    private applyDefaultLabels(): void {
        this.availableLabels = this.defaultLabelItems.map(l => l.label);
        this.labelItems = [...this.defaultLabelItems];
    }

    private loadLabelDefinitions(): void {
        this.unifiedInboxService.getLabelDefinitions().subscribe({
            next: (res: any) => {
                const tags: any[] = res?.data?.tags || [];
                if (tags.length === 0) {
                    // API succeeded but no tags seeded yet — use hardcoded defaults
                    this.applyDefaultLabels();
                    return;
                }
                this.availableLabels = [];
                this.labelItems = [];
                this.labelTagIds = {};
                for (const tag of tags) {
                    const name: string = tag.name;
                    const color: string = tag.tagCategoryColor || '#888888';
                    const tagId: number = tag.tagId;
                    if (name) {
                        this.availableLabels.push(name);
                        this.labelItems.push({ label: name, color });
                        this.labelTagIds[name] = tagId;
                    }
                }
            },
            error: () => {
                // Fallback to hardcoded defaults if API unavailable (e.g. dev with no tags seeded)
                this.applyDefaultLabels();
            }
        });
    }

    ngOnDestroy(): void {
        if (this.sseSource) { this.sseSource.close(); }
        if (this.refreshInterval) { clearInterval(this.refreshInterval); }
        document.removeEventListener('mousemove', this.onResizeMove);
        document.removeEventListener('mouseup',   this.onResizeUp);
    }

    private connectSSE(): void {
        const currentUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
        const token = currentUser?.token || '';
        const url = `${environment.apiUrl}/notifications/stream?token=${encodeURIComponent(token)}`;

        if (this.sseSource) { this.sseSource.close(); }

        this.sseSource = new EventSource(url);

        this.sseSource.addEventListener('new-msg', (e: MessageEvent) => {
            try {
                const data = JSON.parse(e.data);
                if (data.msgContext == null || data.msgContext === this.context) {
                    this.loadConversations();
                }
            } catch {}
        });

        this.sseSource.addEventListener('update-msg', (e: MessageEvent) => {
            try {
                const data = JSON.parse(e.data);
                if (data.msgContext == null || data.msgContext === this.context) {
                    this.loadConversations();
                }
            } catch {}
        });

        // Real-time follow-up badge count pushed by TaskReminderJob every 5 min.
        // Payload: [{"eventType":"TASK_FOLLOWUP_COUNT","userId":<id>,"followupCount":<n>}]
        // Only update if the event is for the currently logged-in user.
        this.sseSource.addEventListener('TASK_FOLLOWUP_COUNT', (e: MessageEvent) => {
            try {
                const raw = JSON.parse(e.data);
                // payload is wrapped in an array by Util.getPojoListToString()
                const item = Array.isArray(raw) ? raw[0] : raw;
                const currentUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
                if (item && item.userId != null && currentUser && item.userId === currentUser.userId) {
                    this.followupCount = item.followupCount != null ? item.followupCount : 0;
                }
            } catch {}
        });

        this.sseSource.onerror = () => {
            if (!this.refreshInterval) {
                this.refreshInterval = setInterval(() => this.loadConversations(), 60000);
            }
            setTimeout(() => {
                if (this.refreshInterval) { clearInterval(this.refreshInterval); this.refreshInterval = null; }
                this.connectSSE();
            }, 30000);
        };
    }

    get filteredConversations(): ConversationSummaryVO[] {
        let list = this.conversations;

        // Apply quick-filter chip
        if (this.activeQuickFilter === 'new-mail') {
            list = list.filter(conv => conv.isRead === false && !conv.isTask);
        } else if (this.activeQuickFilter === 'follow-ups') {
            list = list.filter(conv => conv.isTask === true);
        }

        // Apply nav label filter
        if (this.activeNavLabel) {
            list = list.filter(conv => {
                const labels = this.convLabels[conv.conversationId] || [];
                return labels.includes(this.activeNavLabel);
            });
        }

        // Apply nav channel filter
        if (this.activeNavChannel) {
            const ch = this.activeNavChannel.toUpperCase();
            list = list.filter(conv => {
                const origin = conv.lastOrigin;
                if (ch === 'SMS') { return origin === MsgOrigin.SMS; }
                if (ch === 'EMAIL') { return origin === MsgOrigin.EMAIL || origin === MsgOrigin.ADMIN_INBOX; }
                if (ch === 'SYSTEM') { return origin === MsgOrigin.VRBO_API || origin === MsgOrigin.GUEST_PORTAL; }
                return true;
            });
        }

        // Apply filter panel channel filter
        if (this.filterChannel) {
            const ch = this.filterChannel.toUpperCase();
            list = list.filter(conv => {
                const origin = conv.lastOrigin;
                if (ch === 'EMAIL') { return origin === MsgOrigin.EMAIL || origin === MsgOrigin.ADMIN_INBOX; }
                if (ch === 'SMS') { return origin === MsgOrigin.SMS; }
                if (ch === 'OTA') { return origin === MsgOrigin.VRBO_API; }
                if (ch === 'NOTE') { return origin === MsgOrigin.INTERNAL_NOTE; }
                return true;
            });
        }

        // Apply filter panel status filter
        if (this.filterStatus) {
            if (this.filterStatus === 'unread') {
                list = list.filter(conv => conv.isRead === false);
            } else if (this.filterStatus === 'read') {
                list = list.filter(conv => conv.isRead !== false);
            }
        }

        // Apply property filter — match against subject/preview until backend adds propertyCode to conv list
        if (this.activeNavProperty) {
            const prop = this.activeNavProperty.toLowerCase();
            list = list.filter(conv => {
                const text = ((conv.lastSubject || '') + ' ' + (conv.lastBodyPreview || '') + ' ' + (conv.lastSenderName || '')).toLowerCase();
                return text.includes(prop);
            });
        }

        // Apply text search (client-side for queries under 3 chars)
        const q = (this.searchQuery || '').trim().toLowerCase();
        if (!q) { return list; }
        // When query is 3+ chars, OpenSearch results panel takes over — show full list behind it
        if (q.length >= 3) { return list; }
        return list.filter(conv => {
            return (conv.lastSenderName || '').toLowerCase().includes(q) ||
                   (conv.lastSenderEmail || '').toLowerCase().includes(q) ||
                   (conv.lastSubject || '').toLowerCase().includes(q) ||
                   (conv.lastBodyPreview || '').toLowerCase().includes(q);
        });
    }

    loadConversations(): void {
        this.selectedConvIds.clear();
        this.convLoading = true;
        this.convError = '';
        this.hasMore = false;
        this.oldestDate = null;
        const folderParam = this.activeFolder || undefined;

        const currentUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
        const userId: number | undefined = currentUser?.userId;

        const convReq = this.unifiedInboxService.listConversations({ context: this.context, limit: 50, folder: folderParam });
        const taskReq = this.unifiedInboxService.getDueTasks(userId, this.context).pipe(catchError(() => of(null)));

        forkJoin([convReq, taskReq]).subscribe({
            next: ([convRes, taskRes]) => {
                this.convLoading = false;

                const raw = convRes?.data?.data?.conversations;
                const msgConvs: ConversationSummaryVO[] = Array.isArray(raw) ? raw as ConversationSummaryVO[] : [];
                this.hasMore = convRes?.data?.data?.hasMore === true;
                if (msgConvs.length > 0) {
                    this.oldestDate = msgConvs[msgConvs.length - 1].lastDate;
                }

                // Map due tasks to ConversationSummaryVO rows
                const now = new Date();
                const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                const rawTasks: DueTaskVO[] = taskRes?.data?.data?.tasks || [];
                // Store for selectConversation() lookup
                (this as any)._lastRawTasks = rawTasks;
                const taskConvs: ConversationSummaryVO[] = rawTasks.map((t: DueTaskVO) => {
                    // Parse date-only strings in local time to avoid UTC midnight → prior day shift
                    let followupDate: Date | null = null;
                    if (t.dateToFollowup) {
                        const parts = t.dateToFollowup.split('T')[0].split('-');
                        if (parts.length === 3) {
                            followupDate = new Date(+parts[0], +parts[1] - 1, +parts[2]);
                        } else {
                            followupDate = new Date(t.dateToFollowup);
                        }
                    }
                    const isOverdue = followupDate ? followupDate < today : false;
                    return {
                        conversationId: 'task-' + t.taskId,
                        lastDate: t.dateToFollowup || '',
                        lastSenderName: t.assignedToUserName || '',
                        lastSenderEmail: '',
                        lastSubject: t.name || '',
                        lastBodyPreview: t.description || '',
                        lastOrigin: 'TASK' as any,
                        msgCount: 0,
                        isRead: true,
                        urgency: isOverdue ? 'HIGH' : 'MEDIUM',
                        isTask: true,
                        isOverdue,
                        taskId: t.taskId,
                        taskStatus: t.status,
                        taskStatusLabel: t.statusLabel,
                        taskReservationId: t.reservationId
                    } as ConversationSummaryVO;
                });

                // Merge and sort: unread messages first → overdue tasks → today's tasks → read messages
                const unreadMsgs = msgConvs.filter(c => c.isRead === false);
                const readMsgs   = msgConvs.filter(c => c.isRead !== false);
                const overdueTasks = taskConvs.filter(c => c.isOverdue);
                const todayTasks   = taskConvs.filter(c => !c.isOverdue);

                this.conversations = [
                    ...unreadMsgs,
                    ...overdueTasks,
                    ...todayTasks,
                    ...readMsgs
                ];

                // On first load, mark all existing message conversations as read so only
                // truly new SSE-pushed conversations show the unread dot.
                const isFirstLoad = this.readIds.size === 0;
                if (isFirstLoad) {
                    msgConvs.forEach(c => this.readIds.add(c.conversationId));
                }

                // Auto-select the first non-task conversation (or the first row if all tasks)
                if (this.conversations.length > 0 && !this.activeConversationId) {
                    const firstMsg = this.conversations.find(c => !c.isTask);
                    this.activeConversationId = firstMsg ? firstMsg.conversationId : this.conversations[0].conversationId;
                }

                // L7: update unread count badge on Inbox nav item
                this.mailboxItems[0].count = msgConvs.filter(c => c.isRead === false).length || undefined;
            },
            error: err => {
                this.convLoading = false;
                this.convError = err?.error?.message || 'Failed to load inbox.';
                this.conversations = [];
            }
        });
    }

    /** H11: Load conversations going back up to 365 days by using a far-back cursor date. */
    loadOlder(): void {
        if (this.convLoadingMore) { return; }
        this.convLoadingMore = true;
        const now = new Date();
        const oneYearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        const pad = (n: number) => String(n).padStart(2, '0');
        const before = `${oneYearAgo.getFullYear()}-${pad(oneYearAgo.getMonth() + 1)}-${pad(oneYearAgo.getDate())}T23:59:59`;
        const folderParam = this.activeFolder || undefined;
        this.unifiedInboxService.listConversations({
            context: this.context,
            limit: 50,
            before,
            folder: folderParam
        }).subscribe({
            next: res => {
                this.convLoadingMore = false;
                const raw = res?.data?.data?.conversations;
                const older = Array.isArray(raw) ? raw as ConversationSummaryVO[] : [];
                // Merge without duplicates
                const existingIds = new Set(this.conversations.map(c => c.conversationId));
                const newItems = older.filter(c => !existingIds.has(c.conversationId));
                this.conversations = [...this.conversations, ...newItems];
                this.hasMore = res?.data?.data?.hasMore === true;
                if (older.length > 0) {
                    this.oldestDate = older[older.length - 1].lastDate;
                }
            },
            error: () => { this.convLoadingMore = false; }
        });
    }

    loadMore(): void {
        if (this.convLoadingMore || !this.hasMore || !this.oldestDate) { return; }
        this.convLoadingMore = true;
        const folderParam = this.activeFolder || undefined;
        this.unifiedInboxService.listConversations({
            context: this.context,
            limit: 50,
            before: this.oldestDate,
            folder: folderParam
        }).subscribe({
            next: res => {
                this.convLoadingMore = false;
                const raw = res?.data?.data?.conversations;
                const more = Array.isArray(raw) ? raw as ConversationSummaryVO[] : [];
                this.conversations = [...this.conversations, ...more];
                this.hasMore = res?.data?.data?.hasMore === true;
                if (more.length > 0) {
                    this.oldestDate = more[more.length - 1].lastDate;
                }
            },
            error: () => {
                this.convLoadingMore = false;
            }
        });
    }

    selectConversation(conv: ConversationSummaryVO): void {
        // Task row handling
        if (conv.isTask) {
            // Find the underlying DueTaskVO from the task rows that were fetched
            const taskId = conv.taskId;
            // Retrieve the raw task data we stored on the conv object for the panel
            const rawTasks: DueTaskVO[] = (this as any)._lastRawTasks || [];
            const task = rawTasks.find((t: DueTaskVO) => t.taskId === taskId);

            if (task && task.msgConversationId) {
                // Task has a linked conversation — load that thread
                this.activeConversationId = task.msgConversationId;
                this.activeTaskDetail = null;
                this.activeView = 'conversation';
            } else if (task) {
                // No linked conversation — show task detail panel
                this.activeTaskDetail = task;
                this.activeConversationId = conv.conversationId;
                this.activeView = 'conversation';
            } else {
                // Fallback: task data not available, just highlight the row
                this.activeConversationId = conv.conversationId;
                this.activeTaskDetail = null;
            }
            return;
        }

        // Normal message conversation
        this.activeTaskDetail = null;
        this.activeConversationId = conv.conversationId;
        this.readIds.add(conv.conversationId);
        this.activeView = 'conversation';
        this.contact = null;
        this.guestReservations = [];
        this.guestTasks = [];
        this.unifiedInboxService.markConversationRead(conv.conversationId).subscribe();
        // optimistic: treat as read locally
        conv.isRead = true;
        // Load labels from DB if not already cached for this conversation
        if (!this.convLabels[conv.conversationId]) {
            this.unifiedInboxService.getConversationLabels(conv.conversationId).subscribe({
                next: res => {
                    const labels = res?.data?.data?.labels;
                    this.convLabels[conv.conversationId] = Array.isArray(labels) ? labels : [];
                },
                error: () => { this.convLabels[conv.conversationId] = []; }
            });
        }
    }

    onGuestIdentified(info: { name: string; email: string }): void {
        if (!info.email) { return; }
        const parts = (info.name || '').trim().split(' ');
        this.contact = {
            contactId: null,
            firstName: parts[0] || '',
            lastName: parts.slice(1).join(' ') || '',
            emailAddress: info.email,
            mobileNum: '',
            initials: '',
            isVip: false,
            vipStayCount: 0,
            isGmpVerified: false,
            preferredIsland: '',
            birthday: '',
            memberSince: '',
            lastEmailDate: '',
            lastSmsDate: '',
            lifetimeValue: 0,
            stayCount: 0,
            gmpId: null,
            labels: []
        } as GuestContactVO;

        this.unifiedInboxService.getGuestSummary(info.email, info.name).subscribe({
            next: res => {
                const data = res?.data?.data;
                if (data?.contact) {
                    this.contact = { ...this.contact, ...data.contact };
                }
                // Build labels from real data
                const labels: string[] = [];
                if (this.contact.isVip) { labels.push('VIP'); }
                if (this.contact.stayCount >= 2) { labels.push('Returning Guest'); }
                this.contact.labels = labels;
                this.guestReservations = Array.isArray(data?.reservations) ? data.reservations : [];
                this.guestTasks = Array.isArray(data?.tasks) ? data.tasks : [];
                this.guestQuotes = Array.isArray(data?.quotes) ? data.quotes : [];
                this.guestOrders = Array.isArray(data?.orders) ? data.orders : [];
            },
            error: () => { /* leave partial contact in place, reservations/tasks stay empty */ }
        });
    }

    senderLabel(conv: ConversationSummaryVO): string {
        // Fix 3: Use contactName (the other party) as the primary label when available.
        // This ensures outbound threads don't show "KBM Resorts" as the primary identifier.
        // TODO: backend must add contactName to /api/2.6/msgs/unified response to fully resolve this.
        if (conv.contactName) { return conv.contactName; }

        // Partial fix: if lastSenderName is a known KBM system sender, try the email or fall through.
        const kbmSenders = ['kbm resorts', 'reservations', 'sms', 'system', 'automated'];
        const senderLower = (conv.lastSenderName || '').toLowerCase();
        const isKbmSender = kbmSenders.some(s => senderLower.includes(s));

        if (conv.lastSenderName && !isKbmSender) { return conv.lastSenderName; }
        if (conv.lastSenderEmail) { return conv.lastSenderEmail; }
        if (conv.lastSenderName) { return conv.lastSenderName; }
        switch (conv.lastOrigin) {
            case MsgOrigin.VRBO_API: return 'VRBO';
            case MsgOrigin.SMS: return 'SMS';
            case MsgOrigin.GUEST_PORTAL: return 'Guest Portal';
            case MsgOrigin.INTERNAL_NOTE: return 'Internal Note';
            default: return 'Unknown';
        }
    }

    /** Fix 4: Map lastOrigin to a short display label for the channel badge chip. */
    channelBadgeLabel(conv: ConversationSummaryVO): string {
        if (conv.isTask) { return 'TASK'; }
        const origin = conv.lastOrigin;
        if (origin === MsgOrigin.SMS) { return 'SMS'; }
        if (origin === MsgOrigin.EMAIL || origin === MsgOrigin.ADMIN_INBOX) { return 'EMAIL'; }
        if (origin === MsgOrigin.VRBO_API) { return 'OTA'; }
        if (origin === MsgOrigin.GUEST_PORTAL) { return 'PORTAL'; }
        if (origin === MsgOrigin.INTERNAL_NOTE) { return 'NOTE'; }
        if (origin === MsgOrigin.CALL) { return 'CALL'; }
        return 'MSG';
    }

    /** Fix 4: CSS modifier class for the channel badge chip color. */
    channelBadgeClass(conv: ConversationSummaryVO): string {
        if (conv.isTask) { return 'ch-task'; }
        const origin = conv.lastOrigin;
        if (origin === MsgOrigin.SMS) { return 'ch-sms'; }
        if (origin === MsgOrigin.EMAIL || origin === MsgOrigin.ADMIN_INBOX) { return 'ch-email'; }
        if (origin === MsgOrigin.VRBO_API) { return 'ch-ota'; }
        if (origin === MsgOrigin.GUEST_PORTAL) { return 'ch-portal'; }
        if (origin === MsgOrigin.INTERNAL_NOTE) { return 'ch-note'; }
        if (origin === MsgOrigin.CALL) { return 'ch-call'; }
        return 'ch-default';
    }

    /**
     * Fix 5: Return a secondary property+date line, e.g. "HKK-334 • Aug 2–9".
     * Returns empty string when propertyCode is not available.
     * TODO: backend must add propertyCode, checkInDate, checkOutDate to /api/2.6/msgs/unified
     * conversation list response to enable this display.
     */
    convPropertyLine(conv: ConversationSummaryVO): string {
        if (!conv.propertyCode) { return ''; }
        let line = conv.propertyCode;
        if (conv.checkInDate) {
            const inDate = moment(conv.checkInDate, moment.ISO_8601, true).isValid()
                ? moment(conv.checkInDate)
                : moment(conv.checkInDate, 'YYYY-MM-DD');
            const checkIn = inDate.format('MMM D');
            if (conv.checkOutDate) {
                const outDate = moment(conv.checkOutDate, moment.ISO_8601, true).isValid()
                    ? moment(conv.checkOutDate)
                    : moment(conv.checkOutDate, 'YYYY-MM-DD');
                const checkOut = outDate.format('D');
                line += ' • ' + checkIn + '–' + checkOut;
            } else {
                line += ' • ' + checkIn;
            }
        }
        return line;
    }

    originIcon(origin: number): string {
        switch (origin) {
            case MsgOrigin.SMS: return '💬';
            case MsgOrigin.CALL: return '📞';
            case MsgOrigin.VRBO_API: return '🏠';
            case MsgOrigin.GUEST_PORTAL: return '🌺';
            case MsgOrigin.EMAIL: return '✉️';
            case MsgOrigin.INTERNAL_NOTE: return '📝';
            case MsgOrigin.ADMIN_INBOX: return '✉️';
            default: return '•';
        }
    }

    cleanSubject(subject: string): string {
        if (!subject) { return ''; }
        // Strip raw email thread IDs like "email/15/2026-04-14-17-32-44-283_87e13c4c..."
        if (/^email\/\d+\//.test(subject)) { return ''; }
        // Strip leading "Re: " / "Fwd: " prefixes
        return subject.replace(/^(Re:\s*|Fwd:\s*)+/i, '').trim();
    }

    formatConvDate(value: string): string {
        if (!value) { return ''; }
        const d = new Date(value);
        if (isNaN(d.getTime())) { return value; }
        const now = new Date();
        const sameDay = d.toDateString() === now.toDateString();
        if (sameDay) {
            return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
        }
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }

    getContextName(ctx: number): string {
        const map = {
            [MsgContext.RESERVATIONS]: 'Reservations',
            [MsgContext.HOMEOWNERS]: 'Homeowners',
            [MsgContext.BIZDEV]: 'BizDev',
            [MsgContext.DEVELOPERS]: 'Developers'
        };
        return map[ctx] || 'Messages';
    }

    setView(view: 'conversation' | 'all'): void {
        this.activeView = view;
    }

    /** Folder name map for mailbox items → backend folder param. */
    private readonly mailboxFolderMap: { [mailbox: string]: string } = {
        inbox: '',
        starred: 'STARRED',
        sent: 'SENT',
        archive: 'ARCHIVE',
        drafts: 'DRAFT'
    };

    setMailbox(mailbox: string): void {
        this.activeMailbox = mailbox;
        this.mailboxItems.forEach(i => i.active = i.mailbox === mailbox);

        // Context switch via the context nav items (ctx-N)
        if (mailbox.startsWith('ctx-')) {
            const ctx = parseInt(mailbox.substring(4), 10);
            if (!isNaN(ctx) && ctx !== this.context) {
                this.context = ctx;
                this.contextName = this.getContextName(ctx);
                this.activeConversationId = '';
                this.activeFolder = '';
                this.hasMore = false;
                this.oldestDate = null;
                this.readIds = new Set();
                this.loadConversations();
            }
            return;
        }

        // Mailbox folder switch (Inbox / Starred / Sent / Archive / Drafts)
        const folder = this.mailboxFolderMap[mailbox] ?? '';
        if (folder !== this.activeFolder) {
            this.activeFolder = folder;
            this.activeConversationId = '';
            this.hasMore = false;
            this.oldestDate = null;
            this.loadConversations();
        }
    }

    switchContext(ctx: number): void {
        if (ctx === this.context) { return; }
        this.context = ctx;
        this.contextName = this.getContextName(ctx);
        this.activeConversationId = '';
        this.activeFolder = '';
        this.hasMore = false;
        this.oldestDate = null;
        this.readIds = new Set();
        this.loadConversations();
    }

    toggleLabel(conversationId: string, label: string): void {
        // Optimistic update
        if (!this.convLabels[conversationId]) { this.convLabels[conversationId] = []; }
        const idx = this.convLabels[conversationId].indexOf(label);
        // Optimistic update
        if (idx >= 0) {
            this.convLabels[conversationId].splice(idx, 1);
        } else {
            this.convLabels[conversationId].push(label);
        }
        // Persist to DB
        this.unifiedInboxService.toggleConversationLabel(conversationId, label).subscribe({
            error: () => {
                // Roll back optimistic update on failure
                if (idx >= 0) {
                    this.convLabels[conversationId].splice(idx, 0, label);
                } else {
                    const rollbackIdx = this.convLabels[conversationId].indexOf(label);
                    if (rollbackIdx >= 0) { this.convLabels[conversationId].splice(rollbackIdx, 1); }
                }
            }
        });
    }

    hasLabel(conversationId: string, label: string): boolean {
        return (this.convLabels[conversationId] || []).includes(label);
    }

    openCompose(): void {
        this.composingNew = true;
        this.composeTo = '';
        this.composeSubject = '';
        this.composeBody = '';
        this.composeError = '';
        this.composeSending = false;
    }

    sendCompose(): void {
        if (!this.composeTo.trim() || !this.composeBody.trim()) {
            this.composeError = 'To and Message fields are required.';
            return;
        }
        this.composeSending = true;
        this.composeError = '';
        this.unifiedInboxService.sendEmail({
            conversationId: '',
            msgContext: this.context,
            toEmail: this.composeTo.trim(),
            subject: this.composeSubject.trim() || '(no subject)',
            body: this.composeBody.trim()
        }).subscribe({
            next: () => {
                this.composeSending = false;
                this.composingNew = false;
                this.loadConversations();
            },
            error: (err) => {
                this.composeSending = false;
                this.composeError = err?.error?.message || 'Failed to send message.';
            }
        });
    }

    applyFilters(): void {
        this.filtersOpen = false;
        // Filters are applied reactively via the filteredConversations getter
    }

    clearFilters(): void {
        this.filterChannel = '';
        this.filterStatus = '';
        this.filtersOpen = false;
    }

    setQuickFilter(filter: 'all' | 'new-mail' | 'follow-ups'): void {
        this.activeQuickFilter = filter;
    }

    filterByLabel(label: string): void {
        this.activeNavLabel = this.activeNavLabel === label ? '' : label;
        this.activeNavChannel = '';
        this.activeNavProperty = '';
    }

    filterByChannel(channel: string): void {
        this.activeNavChannel = this.activeNavChannel === channel ? '' : channel;
        this.activeNavLabel = '';
        this.activeNavProperty = '';
    }

    filterByProperty(property: string): void {
        this.activeNavProperty = this.activeNavProperty === property ? '' : property;
        this.activeNavLabel = '';
        this.activeNavChannel = '';
    }

    onConversationArchived(convIdOrAction: string): void {
        if (convIdOrAction.startsWith('unread:')) {
            const convId = convIdOrAction.substring(7);
            const conv = this.conversations.find(c => c.conversationId === convId);
            if (conv) { conv.isRead = false; }
            return;
        }
        // Remove archived conversation from the list
        this.conversations = this.conversations.filter(c => c.conversationId !== convIdOrAction);
        if (this.activeConversationId === convIdOrAction) {
            this.activeConversationId = this.conversations.length > 0 ? this.conversations[0].conversationId : '';
        }
    }

    onSearchChange(): void {
        if (this.searchDebounce) { clearTimeout(this.searchDebounce); }
        if (!this.searchQuery || this.searchQuery.length < 3) {
            this.searchResults = [];
            this.searchError = '';
            return;
        }
        this.searchDebounce = setTimeout(() => {
            this.searchLoading = true;
            this.searchError = '';
            this.unifiedInboxService.searchMessages(this.searchQuery, this.context).subscribe({
                next: res => {
                    // Backend returns { msgIds: number[], totalHits: number }.
                    // Resolve up to 20 IDs to full documents via the opensearch/document endpoint.
                    const raw = res as any;
                    const msgIds: number[] = Array.isArray(raw?.msgIds) ? raw.msgIds.slice(0, 20) : [];
                    if (msgIds.length === 0) {
                        this.searchLoading = false;
                        this.searchResults = [];
                        return;
                    }
                    const docRequests = msgIds.map(id =>
                        this.unifiedInboxService.getMsgDocument(id).pipe(catchError(() => of(null)))
                    );
                    forkJoin(docRequests).subscribe({
                        next: docs => {
                            this.searchLoading = false;
                            this.searchResults = docs.filter(d => d != null);
                        },
                        error: () => {
                            this.searchLoading = false;
                            this.searchError = 'Search failed.';
                            this.searchResults = [];
                        }
                    });
                },
                error: () => {
                    this.searchLoading = false;
                    this.searchError = 'Search failed.';
                    this.searchResults = [];
                }
            });
        }, 400);
    }

    selectSearchResult(result: any): void {
        const convId = result.msgConversationId || result.conversationId;
        if (convId) {
            this.activeConversationId = convId;
            this.activeView = 'conversation';
            this.searchQuery = '';
            this.searchResults = [];
        }
    }

    get hasUnread(): boolean {
        return this.conversations.some(c => c.isRead === false);
    }

    isConvUnread(conv: ConversationSummaryVO): boolean {
        return conv.isRead === false;
    }

    openManageLabels(): void {
        this.manageLabelsOpen = true;
        this.manageLabelNewName = '';
    }

    closeManageLabels(): void {
        this.manageLabelsOpen = false;
        this.manageLabelNewName = '';
    }

    addManagedLabel(): void {
        const name = this.manageLabelNewName.trim();
        if (!name || this.availableLabels.includes(name)) { return; }
        this.unifiedInboxService.createLabelDefinition(name).subscribe({
            next: (res: any) => {
                const saved = res?.data?.tag;
                const tagId: number = saved?.tagId;
                const color: string = saved?.tagCategoryColor || '#888888';
                this.availableLabels = [...this.availableLabels, name];
                this.labelItems = [...this.labelItems, { label: name, color }];
                if (tagId) { this.labelTagIds[name] = tagId; }
                this.manageLabelNewName = '';
            },
            error: () => {
                // Silently fail — do not add to in-memory list if server rejected
            }
        });
    }

    deleteManagedLabel(label: string): void {
        const tagId = this.labelTagIds[label];
        if (tagId) {
            this.unifiedInboxService.deleteLabelDefinition(tagId).subscribe({
                next: () => {
                    this.availableLabels = this.availableLabels.filter(l => l !== label);
                    this.labelItems = this.labelItems.filter(l => l.label !== label);
                    delete this.labelTagIds[label];
                },
                error: () => {
                    // Silently fail — list stays unchanged
                }
            });
        } else {
            // No tagId known (e.g. fallback mode) — remove from local only
            this.availableLabels = this.availableLabels.filter(l => l !== label);
            this.labelItems = this.labelItems.filter(l => l.label !== label);
        }
    }

    getUrgency(conv: ConversationSummaryVO): 'urgent' | 'high' | null {
        if (conv.urgency) {
            const u = conv.urgency.toUpperCase();
            if (u === 'URGENT') { return 'urgent'; }
            if (u === 'HIGH') { return 'high'; }
            return null;
        }
        // client-side fallback for messages before backend classifier ran
        const text = ((conv.lastSubject || '') + ' ' + (conv.lastBodyPreview || '')).toLowerCase();
        const urgentTerms = ['urgent', 'emergency', 'immediately', 'asap', 'right now', 'critical', 'stranded', 'injured', 'fire', 'flood', 'locked out'];
        const highTerms = ['complaint', 'unhappy', 'disappointed', 'not working', 'broken', 'refund', 'cancel', 'problem', 'issue', 'wrong', 'error', 'failed'];
        if (urgentTerms.some(t => text.includes(t))) { return 'urgent'; }
        if (highTerms.some(t => text.includes(t))) { return 'high'; }
        return null;
    }

    selectedConvIds: Set<string> = new Set();
    showBulkLabelMenu = false;

    get someSelected(): boolean { return this.selectedConvIds.size > 0; }
    get allSelected(): boolean { return this.conversations.length > 0 && this.selectedConvIds.size === this.conversations.length; }

    isSelected(conv: ConversationSummaryVO): boolean { return this.selectedConvIds.has(conv.conversationId); }

    toggleSelection(conv: ConversationSummaryVO, event: Event): void {
        event.stopPropagation();
        if (this.selectedConvIds.has(conv.conversationId)) {
            this.selectedConvIds.delete(conv.conversationId);
        } else {
            this.selectedConvIds.add(conv.conversationId);
        }
    }

    selectAll(): void { this.conversations.forEach(c => this.selectedConvIds.add(c.conversationId)); }
    clearSelection(): void { this.selectedConvIds.clear(); }

    bulkMarkRead(): void {
        this.selectedConvIds.forEach(id => {
            this.unifiedInboxService.markConversationRead(id).subscribe();
            const conv = this.conversations.find(c => c.conversationId === id);
            if (conv) { conv.isRead = true; }
        });
        this.clearSelection();
    }

    bulkLabel(label: string): void {
        this.selectedConvIds.forEach(id => {
            if (!this.convLabels[id]) { this.convLabels[id] = []; }
            if (!this.convLabels[id].includes(label)) {
                this.convLabels[id].push(label);
                this.unifiedInboxService.toggleConversationLabel(id, label).subscribe();
            }
        });
        this.clearSelection();
    }
}
